import React, {useEffect, useState} from 'react';
import {Table, TablesTabProps} from '../../dashboard/types';
import {TableMap} from './TableMap';

export const TablesTab: React.FC<TablesTabProps> = ({
  tables,
  onAddTable,
  onEditTable,
  onDeleteTable,
}) => {
  const [filteredTables, setFilteredTables] = React.useState(tables);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<{
    tableNumber: string;
    isVip: boolean;
    position: { x?: number; y?: number };
    reserved: boolean;
    capacity: number;
    shape: 'round' | 'rectangular';
  }>({tableNumber: '', isVip: false, position: {x: undefined, y: undefined}, reserved: false, capacity: 0, shape: 'round'});

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value.toLowerCase();
    if (searchTerm === '' || !searchTerm) {
      setFilteredTables(tables);
      return;
    } else {
      const filteredTables = tables.filter(
        (table) =>
          table?.tableNumber.toString().toLowerCase().includes(searchTerm) ||
          table?.shape.toLowerCase().includes(searchTerm) ||
          table?.capacity.toString().includes(searchTerm) ||
          (table?.isVip ? 'vip' : 'regular').includes(searchTerm) ||
          (table?.reserved ? 'reserved' : 'available').includes(searchTerm),
      );
      setFilteredTables(filteredTables);
    }
    // Update the displayed tables based on the search term
    // This could be done by lifting the state up to the parent component
  };
  const handleMapPick = (x: number, y: number) => {
    setForm((prev) => ({...prev, position: {x, y}}));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !form.tableNumber ||
      form.position.x === undefined ||
      form.position.y === undefined
    )
      return;
    onAddTable(form);
    setForm({
      tableNumber: '',
      isVip: false,
      position: {x: undefined, y: undefined},
      reserved: false,
      capacity: 0,
      shape: 'round'

    });
    setShowForm(false);
  };

  useEffect(() => {
    setFilteredTables(tables);
  }, [tables]);
  return (
    <section className='table-card'>
      <div className='table-card-header flex justify-between items-center mb-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4'>
        <div className='rounded-lg p-3 bg-blue-50 border border-blue-100 shadow'>
          <span className='text-sm text-blue-500'>Total Tables</span>
          <p className='text-2xl font-bold'>{tables.length}</p>
        </div>
        <div className='rounded-lg p-3 bg-blue-50 border border-blue-100 shadow'>
          <span className='text-sm text-blue-500'>Total VIP Tables</span>
          <p className='text-2xl font-bold'>
            {tables.filter((table) => table.isVip).length}
          </p>
        </div>
        <div className='rounded-lg p-3 bg-blue-50 border border-blue-100 shadow'>
          <span className='text-sm text-blue-500'>Total Regular Tables</span>
          <p className='text-2xl font-bold'>
            {tables.filter((table) => !table.isVip).length}
          </p>
        </div>
        <div className='rounded-lg p-3 bg-blue-50 border border-blue-100 shadow'>
          <span className='text-sm text-blue-500'>Total Reserved</span>
          <p className='text-2xl font-bold'>
            {tables.filter((table) => table.reserved).length}
          </p>
        </div>
      </div>

      <header className='flex justify-between items-center'>
        <h2>Tables</h2>
        <input
          type='text'
          placeholder='Search tables...'
          className='border px-2 py-1 rounded flex-grow mx-4'
          onChange={handleSearch}
        />
        <button
          className='bg-blue-500 text-white px-4 py-2 rounded'
          onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'Add Table'}
        </button>
      </header>

      {showForm && (
        <form
          className='mb-6 p-4 border rounded bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-4'
          onSubmit={handleSubmit}>
          <div className='mb-2'>
            <label className='block mb-1'>Table Number</label>
            <input
              className='border px-2 py-1 rounded w-full'
              value={form.tableNumber}
              onChange={(e) =>
                setForm((f) => ({...f, tableNumber: e.target.value}))
              }
              required
            />
          </div>
          <div className='mb-2'>
            <label className='block mb-1'>Capacity</label>
            <input
              type='number'
              className='border px-2 py-1 rounded w-full'
              value={form.capacity}
              onChange={(e) =>
                setForm((f) => ({...f, capacity: parseInt(e.target.value, 10)}))
              }
              required
            />
          </div>
          <div className='mb-2'>
            <label className='block mb-1'>Table Shape</label>
            <select id='shape' name='shape' className='px-2 py-1 border rounded w-full'
              value={form.shape}
              onChange={(e) =>
                setForm((f) => ({...f, shape: e.target.value as unknown as 'round' | 'rectangular'}))
              }>
              <option value='round' className='px-3 '>Round</option>
              <option value='rectangular' className='px-3 '>Rectangular</option>
            </select>
          </div>

          <div className='mb-2'>
            <label className='block mb-1'>VIP Table?</label>
            <input
              type='checkbox'
              checked={form.isVip}
              onChange={(e) =>
                setForm((f) => ({...f, isVip: e.target.checked}))
              }
            />
          </div>
          <div className='mb-2 col-span-full'>
            <label className='block mb-1'>Pick Position on Map</label>
            <TableMap
              onPickPosition={handleMapPick}
              selectedPosition={
                form.position?.x !== undefined && form.position?.y !== undefined
                  ? {x: form.position.x, y: form.position.y}
                  : undefined
              }
              tables={
                form.isVip
                  ? tables.filter((table) => table.isVip)
                  : tables.filter((table) => !table.isVip)
              }
            />
            {form.position?.x !== undefined &&
              form.position?.y !== undefined && (
                <div className='text-xs mt-1 text-gray-600'>
                  Picked: ({Math.round(form.position.x)},{' '}
                  {Math.round(form.position.y)})
                </div>
              )}
          </div>
          <button
            type='submit'
            className='bg-green-500 text-white px-4 py-2 rounded mt-2 w-6/12'>
            Save Table
          </button>
        </form>
      )}

      <div className='table-wrap'>
        <table>
          <thead>
            <tr>
              <th>Table Number</th>
              <th>Shape</th>
              <th>Capacity</th>
              <th>Position</th>
              <th>VIP</th>
              <th>Reserved</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredTables.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className='empty-row'>
                  No tables found.
                </td>
              </tr>
            ) : (
              filteredTables.map((table: Table) => (
                <tr key={table.id}>
                  <td>{table.tableNumber}</td>
                  <td>{table.shape}</td>
                  <td>{table.capacity}</td>
                  <td>
                    ({Math.round(table.position?.x ?? 0)},{' '}
                    {Math.round(table.position?.y ?? 0)})
                  </td>
                  <td>{table.isVip ? 'Yes' : 'No'}</td>
                  <td>{table.reserved ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      className='mr-2'
                      onClick={() => onEditTable(table?.id)}>
                      Edit
                    </button>
                    <button
                      className='text-red-500'
                      onClick={() => onDeleteTable(table?.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
